// Business Settings — single source of truth for the Financial Reporting
// Start Date (the date Silver Sage operations effectively began, defaulting
// to the first successful live Stripe payment).
//
// Every financial dashboard, KPI, projection, and MAVIS insight reads from
// here so numbers can never disagree about which time window they represent.

import { supabase } from "@/integrations/supabase/client";

export interface BusinessSettings {
  reportingStartDate: string; // ISO date "YYYY-MM-DD"
  includeHistorical: boolean;
  updatedAt: string | null;
}

export const DEFAULT_REPORTING_START_DATE = "2026-07-01";

export const DEFAULT_BUSINESS_SETTINGS: BusinessSettings = {
  reportingStartDate: DEFAULT_REPORTING_START_DATE,
  includeHistorical: false,
  updatedAt: null,
};

export async function fetchBusinessSettings(): Promise<BusinessSettings> {
  const { data } = await (supabase as any)
    .from("business_settings")
    .select("reporting_start_date, include_historical, updated_at")
    .eq("id", true)
    .maybeSingle();
  if (!data) return DEFAULT_BUSINESS_SETTINGS;
  return {
    reportingStartDate: data.reporting_start_date ?? DEFAULT_REPORTING_START_DATE,
    includeHistorical: !!data.include_historical,
    updatedAt: data.updated_at ?? null,
  };
}

export async function saveBusinessSettings(patch: {
  reportingStartDate?: string;
  includeHistorical?: boolean;
}): Promise<void> {
  const payload: Record<string, unknown> = { id: true };
  if (patch.reportingStartDate != null) payload.reporting_start_date = patch.reportingStartDate;
  if (patch.includeHistorical != null) payload.include_historical = patch.includeHistorical;
  const { error } = await (supabase as any)
    .from("business_settings")
    .upsert(payload, { onConflict: "id" });
  if (error) throw error;
}

/**
 * Returns true when a transaction with the given ISO timestamp should be
 * INCLUDED in financial analytics under the current settings.
 */
export function isWithinReportingWindow(
  timestamp: string | Date | null | undefined,
  settings: BusinessSettings,
): boolean {
  if (settings.includeHistorical) return true;
  if (!timestamp) return false;
  const t = typeof timestamp === "string" ? new Date(timestamp) : timestamp;
  if (Number.isNaN(t.getTime())) return false;
  const cutoff = new Date(`${settings.reportingStartDate}T00:00:00Z`);
  return t.getTime() >= cutoff.getTime();
}

export function formatReportingStartLabel(settings: BusinessSettings): string {
  if (settings.includeHistorical) return "Reporting includes all historical data.";
  const d = new Date(`${settings.reportingStartDate}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return "";
  const label = d.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
  return `Reporting since ${label}.`;
}
